import React from 'react'
import { Trans, useLingui } from '@lingui/react/macro'

import { Card } from '@newComponents/Card'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'

const Security = () => {
  const { t } = useLingui()

  return (
    <>
      <Card gap='$4'>
        <Paragraph size='$5'>
          <Trans>
            Guild is designed with security in mind. The default sign-in flow
            uses a one-time verification code sent to your email address. You
            can also set a password in your account settings if you prefer email
            and password sign-in.
          </Trans>
        </Paragraph>
        <DocScreenshot
          src='docs/account/sign-in-page.png'
          alt='Sign in page with email input for requesting a verification code'
          caption={t`Guild signs you in with an email verification code by default`}
        />
      </Card>

      <Card gap='$4'>
        <DocSection id='email-code-auth' title={t`Email Code Authentication`}>
          <Paragraph size='$5'>
            <Trans>
              When you sign in to Guild, you receive a one-time 5-digit
              verification code via email. This code expires after 10 minutes
              and can only be used once.
            </Trans>
          </Paragraph>
          <Paragraph size='$5'>
            <Trans>
              For the full sign-in walkthrough, see{' '}
              <Link href='/docs/account/sign-in'>Sign Up & Sign In</Link>.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='passwords' title={t`Passwords`}>
          <Paragraph size='$5'>
            <Trans>
              Passwords are optional. After signing in, you can open your
              account settings to set or change a password. Once a password is
              set, you can sign in with your email and password instead of an
              email verification code.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='sessions' title={t`Session Management`}>
          <Paragraph size='$5'>
            <Trans>
              After signing in, Guild creates a session that keeps you
              authenticated as you browse the platform. Sessions are maintained
              securely using HTTP-only cookies that are not accessible to
              JavaScript, protecting against cross-site scripting attacks.
            </Trans>
          </Paragraph>
          <Paragraph size='$5'>
            <Trans>
              You can sign out at any time by clicking your profile photo in the
              top-right corner and selecting{' '}
              <Paragraph size='$5' fontWeight='$7'>
                Sign Out
              </Paragraph>
              . This ends your current session immediately.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='connected-accounts' title={t`Connected Services`}>
          <Paragraph size='$5'>
            <Trans>
              Google account connections are used for{' '}
              <Link href='/docs/account/calendar'>Calendar Sync</Link>, which
              automatically adds Events to your Google Calendar when you RSVP.
              Calendar connections are managed separately from sign-in methods
              in your account Settings.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='data-privacy' title={t`Your Data`}>
          <Paragraph size='$5'>
            <Trans>
              Guild only collects the information needed to provide the service
              — your email address, display name, and any profile details you
              choose to add. Your email address is used for authentication and
              notifications, and is not shared publicly unless you choose to
              display it.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>

      <Card gap='$4'>
        {/* TODO: Screenshot of account deletion flow — this is admin-initiated so not easily capturable from the user's perspective */}
        <DocSection id='account-deletion' title={t`Account Deletion`}>
          <Paragraph size='$5'>
            <Trans>
              If you wish to delete your account, contact a Guild administrator.
              When your account is deleted, all your personal data is
              permanently removed from the platform, including your profile
              information, event attendance history, and any content you
              created.
            </Trans>
          </Paragraph>
          <Paragraph size='$5'>
            <Trans>
              Before deletion, an administrator can optionally send you an email
              with a full export of your data, including your profile details,
              Guild memberships, Event attendance records, and Presentations.
              This ensures you have a copy of your data before it is permanently
              removed.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>
    </>
  )
}

export default Security
